import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XIcon } from 'lucide-react';
function LegalModal({
  isOpen,
  onClose,
  title,
  children
}: {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
}) {
  return <AnimatePresence data-id="element-178">
      {isOpen && <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.25
    }} className="fixed inset-0 z-[1100] flex items-center justify-center p-4" onClick={onClose} data-id="element-179">
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" data-id="element-180" />
          <motion.div initial={{
        opacity: 0,
        y: 40,
        scale: 0.97
      }} animate={{
        opacity: 1,
        y: 0,
        scale: 1
      }} exit={{
        opacity: 0,
        y: 40,
        scale: 0.97
      }} transition={{
        duration: 0.3,
        ease: 'easeOut'
      }} onClick={e => e.stopPropagation()} className="relative bg-warm-white rounded-xl shadow-2xl w-full max-w-[680px] max-h-[80vh] overflow-hidden flex flex-col" data-id="element-181">
            <div className="flex items-center justify-between px-8 py-5 border-b border-black/10 bg-cream-light" data-id="element-182">
              <h2 className="font-serif text-[24px] text-primary-dark" data-id="element-183">
                {title}
              </h2>
              <button onClick={onClose} className="p-2 rounded-lg hover:bg-black/5 transition-colors" aria-label="Close" data-id="element-184">
                <XIcon className="w-5 h-5 text-primary-dark/60" data-id="element-185" />
              </button>
            </div>
            <div className="overflow-y-auto px-8 py-6 font-sans text-[15px] text-primary-dark/80 leading-[1.9]" data-id="element-186">
              {children}
            </div>
          </motion.div>
        </motion.div>}
    </AnimatePresence>;
}
export function FooterSection() {
  const [showPrivacy, setShowPrivacy] = useState(false);
  const [showTerms, setShowTerms] = useState(false);
  return <>
      <footer className="bg-[#080f0a] pt-[60px] pb-[40px] px-[40px] text-center" data-id="element-187">
        <div className="max-w-[800px] mx-auto" data-id="element-188">
          <h3 className="font-sans text-[18px] font-bold text-cream-light mb-2" data-id="element-189">
            GoDeskCore · PROSPERA
          </h3>

          <p className="font-sans text-[14px] text-cream-light/60 mb-8" data-id="element-190">
            Building financial systems for people the financial industry forgot.
          </p>

          <div className="flex items-center justify-center gap-6 mb-8" data-id="element-191">
            <button onClick={() => setShowPrivacy(true)} className="font-sans text-[13px] text-cream-light/50 hover:text-gold-accent transition-colors underline underline-offset-2" data-id="element-192">
              Privacy Policy
            </button>
            <span className="text-cream-light/20" data-id="element-193">·</span>
            <button onClick={() => setShowTerms(true)} className="font-sans text-[13px] text-cream-light/50 hover:text-gold-accent transition-colors underline underline-offset-2" data-id="element-194">
              Terms of Service
            </button>
          </div>

          <div className="w-full h-px bg-white/5 mb-8" data-id="element-195"></div>

          <p className="font-sans text-[12px] text-cream-light/35 mb-4" data-id="element-196">
            © 2025 GoDeskCore / Prospera. All rights reserved.
          </p>

          <p className="font-sans text-[11px] text-cream-light/30 max-w-[600px] mx-auto leading-relaxed" data-id="element-197">
            Results mentioned reflect real user experiences. Individual results
            vary based on personal circumstances and actions taken. This is a
            digital product. No physical item is shipped.
          </p>
        </div>
      </footer>

      {/* Privacy Policy Modal */}
      <LegalModal isOpen={showPrivacy} onClose={() => setShowPrivacy(false)} title="Privacy Policy" data-id="element-198">
        <p className="text-[12px] text-primary-dark/40 mb-6" data-id="element-199">
          Last updated: March 2025
        </p>

        <h3 className="font-serif text-[18px] text-primary-dark mb-3" data-id="element-200">
          1. Information We Collect
        </h3>
        <p className="mb-6" data-id="element-201">
          When you purchase The Debt-Free System, we collect only the
          information necessary to process your transaction and deliver your
          digital product. This includes your email address and payment
          information. Payment processing is handled by our third-party payment
          processor — we do not store your credit card details on our servers.
        </p>

        <h3 className="font-serif text-[18px] text-primary-dark mb-3" data-id="element-202">
          2. How We Use Your Information
        </h3>
        <p className="mb-4" data-id="element-203">We use the information we collect to:</p>
        <ul className="list-disc pl-6 mb-6 space-y-2" data-id="element-204">
          <li data-id="element-205">Deliver your purchased digital product (PDF ebook)</li>
          <li data-id="element-206">Send your purchase confirmation and download link</li>
          <li data-id="element-207">Process refund requests if applicable</li>
          <li data-id="element-208">Respond to customer support inquiries</li>
        </ul>
        <p className="mb-6" data-id="element-209">
          We do not sell, rent, or share your personal information with third
          parties for marketing purposes.
        </p>

        <h3 className="font-serif text-[18px] text-primary-dark mb-3" data-id="element-210">
          3. Cookies & Analytics
        </h3>
        <p className="mb-6" data-id="element-211">
          Our website may use cookies and analytics tools to understand how
          visitors interact with our pages. This data is aggregated and
          anonymous. You can disable cookies in your browser settings at any
          time.
        </p>

        <h3 className="font-serif text-[18px] text-primary-dark mb-3" data-id="element-212">
          4. Data Security
        </h3>
        <p className="mb-6" data-id="element-213">
          We implement industry-standard security measures to protect your
          personal information. All transactions are encrypted using SSL
          technology. However, no method of transmission over the Internet is
          100% secure, and we cannot guarantee absolute security.
        </p>

        <h3 className="font-serif text-[18px] text-primary-dark mb-3" data-id="element-214">
          5. Your Rights
        </h3>
        <p className="mb-6" data-id="element-215">
          You have the right to request access to, correction of, or deletion of
          your personal data at any time. To exercise these rights, contact us
          at the email provided on our website. We will respond to your request
          within 30 days.
        </p>

        <h3 className="font-serif text-[18px] text-primary-dark mb-3" data-id="element-216">
          6. Changes to This Policy
        </h3>
        <p className="mb-2" data-id="element-217">
          We may update this Privacy Policy from time to time. Any changes will
          be posted on this page with an updated revision date. Your continued
          use of our website after any changes constitutes acceptance of the
          updated policy.
        </p>
      </LegalModal>

      {/* Terms of Service Modal */}
      <LegalModal isOpen={showTerms} onClose={() => setShowTerms(false)} title="Terms of Service" data-id="element-218">
        <p className="text-[12px] text-primary-dark/40 mb-6" data-id="element-219">
          Last updated: March 2025
        </p>

        <h3 className="font-serif text-[18px] text-primary-dark mb-3" data-id="element-220">
          1. Product Description
        </h3>
        <p className="mb-6" data-id="element-221">
          The Debt-Free System is a digital ebook (PDF format) that includes
          educational content, financial worksheets, templates, and negotiation
          scripts. It is designed as a self-guided financial education resource.
          This product does not constitute personalized financial advice, and
          GoDeskCore / PROSPERA is not a licensed financial advisor.
        </p>

        <h3 className="font-serif text-[18px] text-primary-dark mb-3" data-id="element-222">
          2. Digital Product Delivery
        </h3>
        <p className="mb-6" data-id="element-223">
          Upon successful payment, you will receive instant access to download
          the complete digital product. The product is delivered as a PDF file
          compatible with all major devices and operating systems. No physical
          product is shipped.
        </p>

        <h3 className="font-serif text-[18px] text-primary-dark mb-3" data-id="element-224">
          3. Refund Policy — The "Chapter 5" Guarantee
        </h3>
        <p className="mb-6" data-id="element-225">
          We offer a full refund if you use the Chapter 5 negotiation scripts
          and do not save more than the purchase price ($9.99) on your first
          call. To request a refund, simply send one email to our support
          address within 30 days of purchase. No forms or explanations required.
          Refunds are processed within 24 hours.
        </p>

        <h3 className="font-serif text-[18px] text-primary-dark mb-3" data-id="element-226">
          4. Intellectual Property
        </h3>
        <p className="mb-6" data-id="element-227">
          All content within The Debt-Free System — including text, worksheets,
          templates, scripts, and design — is the intellectual property of
          GoDeskCore / PROSPERA. Your purchase grants you a personal,
          non-transferable license to use the materials for your own financial
          planning. You may not reproduce, distribute, resell, or share the
          product or its contents.
        </p>

        <h3 className="font-serif text-[18px] text-primary-dark mb-3" data-id="element-228">
          5. Earnings & Results Disclaimer
        </h3>
        <p className="mb-6" data-id="element-229">
          Results mentioned on this page reflect real user experiences but are
          not guaranteed. Individual results vary based on personal financial
          circumstances, income, debt levels, and actions taken. The savings
          figures cited (e.g., $50–$100/month from negotiation calls) represent
          typical outcomes but are not a promise of specific results.
        </p>

        <h3 className="font-serif text-[18px] text-primary-dark mb-3" data-id="element-230">
          6. Limitation of Liability
        </h3>
        <p className="mb-6" data-id="element-231">
          GoDeskCore / PROSPERA provides this product for educational purposes
          only. We are not responsible for any financial decisions you make
          based on the content of this ebook. By purchasing, you acknowledge
          that you are solely responsible for your financial actions and
          outcomes.
        </p>

        <h3 className="font-serif text-[18px] text-primary-dark mb-3" data-id="element-232">
          7. Governing Law
        </h3>
        <p className="mb-2" data-id="element-233">
          These terms are governed by and construed in accordance with the laws
          of the United States. Any disputes arising from these terms or your
          use of the product shall be resolved through binding arbitration.
        </p>
      </LegalModal>
    </>;
}